// Selector construction for the `data-toggle-prefix` mechanism (see toggle_prefix.js).
//
// A toggle class pairs a developer-authored prefix with the *value* of the control that
// drives the toggle: `${prefix}-${value}`. The prefix is written in code, but the value
// comes from the form, so it is whatever a select option or button-group option carries —
// and some of them contain a space. The background overlay's gradient direction offers
// `top right` and `bottom right`; ButtonGroupComponent turns each option value into a
// revealable-content class.
//
// One space breaks three things at once:
//
//   1. `class="prefix-top right"` is *two* classes in the DOM, so `.prefix-top right`
//      never matches the container it was supposed to reveal.
//   2. `[class!=prefix-top right]:not(.prefix-top right)` is not a valid selector.
//      jQuery throws "Syntax error, unrecognized expression", and because that throw
//      escapes the per-prefix loop in toggle_prefix.js, every *other* prefix on the same
//      control silently stops toggling too.
//   3. RevealableContentContainerComponent renders the same string as an element `id`,
//      and an id containing whitespace can never be found by getElementById.
//
// Normalizing the value to a single CSS-identifier-safe token fixes all three. The Ruby
// side that renders these classes and ids applies the identical rule — see
// ButtonGroupComponent#toggle_class_token — so the two always agree on the token.

const UNSAFE_IN_CSS_IDENTIFIER = /[^a-zA-Z0-9_-]+/g

// Kept faithful to the template literal this replaced: a non-string value (a boolean from
// a checkbox, a null from an empty select) stringifies the same way it always did.
export const toggleClassToken = (prefix, value) =>
  `${prefix}-${String(value).replace(UNSAFE_IN_CSS_IDENTIFIER, '-')}`

// Everything carrying the prefix except the one the current value selects. `[class!=…]` is
// a jQuery extension, not standard CSS.
export const hideSelector = (prefix, value) => {
  const shown = toggleClassToken(prefix, value)
  const shownOnlyHide = toggleClassToken(`${prefix}OnlyHide`, value)

  return (
    `[class*=${prefix}-][class!=${shown}]:not(.${shown}), ` +
    `[class*=${prefix}OnlyHide-][class!=${shownOnlyHide}]:not(.${shownOnlyHide})`
  )
}

export const showSelector = (prefix, value) =>
  `.${toggleClassToken(prefix, value)}`

export const cascadeSelector = (prefix, value) =>
  `.${toggleClassToken(`${prefix}Cascade`, value)}`
